import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { audit } from '@cwos/api-client';
import type { audit as AuditNS } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, useFocusTrap } from '@cwos/ui-admin';
import { formatCount } from '@cwos/ui-shared';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { SettingsCard, SettingsHeader } from '../settings/common.js';
import {
  EMPTY_COPY,
  RANGE_LABELS,
  actionOptions,
  actorFilterNote,
  actorHint,
  emptyReason,
  exportOutcome,
  exportScopeLabel,
  formatEventTime,
  groupByDay,
  looksLikePayload,
  resolveRange,
  scopeQuery,
  sensitivityBadge,
  targetLine,
  targetNote,
  type RangePreset,
} from './auditLogic.js';
import { LoadFailure } from '../LoadFailure.js';
import { downloadTextFile } from '../../lib/downloadCsv.js';

const PAGE_SIZE = 50;

/**
 * The Audit Log — "what happened in my organization, who did it, and when".
 *
 * Every row is business language produced by the server's projection layer.
 * There is no raw JSON view, no UUID column, and no "show raw event" affordance:
 * what a viewer may be told was decided server-side, and this screen renders
 * that decision rather than re-deciding it.
 *
 * Pagination is LOAD-MORE over a keyset cursor. Audit history grows while
 * someone reads it, and numbered pages over a growing table silently skip and
 * repeat rows — which is exactly the flaw that makes an evidence trail
 * untrustworthy. Loading forward from a cursor cannot.
 */
export function AuditLog() {
  const permissions = usePermissions();
  /**
   * FOUR ANSWERS, NOT TWO. `has('audit:read') === false` also means "the set has
   * not arrived" and "the set could not be read", and the card below says "you
   * don't have access to the activity log" — an accusation.
   *
   * THIS BRANCH IS LATENT AND IS FIXED ANYWAY. `audit:read` is also the key the
   * ROUTE is gated on, so `RequirePermission` answers the unresolved set with a
   * skeleton and the unreadable set with its own error panel, and neither state
   * reaches here today. It is dead because two keys happen to be identical, not
   * because the site is right, and the day the route key and the screen key
   * diverge it wakes up saying the wrong thing. Recording it as latent is the
   * reason to fix it cheaply, not the reason to leave it.
   */
  const readGate = authorityGate(permissions, 'audit:read');
  const canRead = gateOpen(readGate);

  const [preset, setPreset] = useState<RangePreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [pages, setPages] = useState<AuditNS.AuditPage[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<AuditNS.AuditEvent | null>(null);

  // `now` is pinned per render pass so the range shown, the range queried and
  // the range exported cannot disagree by a few milliseconds.
  const now = useMemo(() => new Date(), []);
  const range = resolveRange(preset, now, { from: customFrom, to: customTo });

  // A category and a single action are mutually exclusive at the API; the
  // narrower of the two wins on the wire while both stay visible on screen.
  const scope = scopeQuery(category, action);
  const baseQuery: AuditNS.AuditQuery = {
    ...range,
    category: scope.category as AuditNS.AuditCategory | undefined,
    action: scope.action,
    actorUserId: actorUserId || undefined,
    limit: PAGE_SIZE,
  };

  const list = useQuery({
    queryKey: ['audit', 'events', baseQuery, cursor],
    queryFn: async () => {
      const page = await audit.events({ ...baseQuery, cursor });
      // A response that is not a page — see `looksLikePayload` — must not reach
      // the accumulator, or every later render dereferences it.
      if (!looksLikePayload<AuditNS.AuditPage>(page, 'events')) {
        throw new Error('The activity log returned an unexpected response.');
      }
      // Accumulate forward; a filter change resets `pages` via the key below.
      setPages((prev) => (cursor ? [...prev, page] : [page]));
      return page;
    },
    enabled: canRead,
  });

  const filters = useQuery({
    queryKey: ['audit', 'filters', range.from ?? null, range.to ?? null],
    queryFn: () => audit.filters(range),
    enabled: canRead,
  });

  const events = pages.flatMap((p) => p.events);
  const lastPage = pages[pages.length - 1];
  const grouped = useMemo(() => groupByDay(events, now), [events, now]);

  function changeFilters(next: () => void) {
    next();
    // A new scope is a new result set, not more of the old one.
    setPages([]);
    setCursor(undefined);
    setSelected(null);
  }

  if (!gateOpen(readGate)) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <SettingsHeader title="Audit log" />
        <GateNotice
          gate={readGate}
          what="see organization-wide activity"
          onRetry={() => permissions.refetch()}
          heading="You don’t have access to the activity log"
          detail="Viewing organization-wide activity needs the audit permission, which is held by organization administrators. Ask an administrator if you need it."
        />
      </div>
    );
  }

  const filtersUnavailable = filters.isError && filters.data === undefined;
  const categoryLabel = filters.data?.categories.find((c) => c.key === category)?.label;
  const actorLabel = filters.data?.actors.find((a) => a.userId === actorUserId)?.label;
  const actions = actionOptions(filters.data?.actions, category);
  const actionLabel = actions.find((a) => a.action === action)?.label;
  const actorNote = actorFilterNote(filters.data);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Audit log"
        blurb="Every consequential action in your organization: who did it, what it affected, and when. Records cannot be edited or deleted."
      />

      <SettingsCard>
        <div className="cmp-filters" role="search">
          <label className="cmp-filter">
            <span className="rn-muted">Period</span>
            <select
              style={{ marginBottom: 0 }}
              value={preset}
              onChange={(e) => changeFilters(() => setPreset(e.target.value as RangePreset))}
            >
              {(Object.keys(RANGE_LABELS) as RangePreset[]).map((key) => (
                <option key={key} value={key}>
                  {RANGE_LABELS[key]}
                </option>
              ))}
            </select>
          </label>

          {preset === 'custom' ? (
            <>
              <label className="cmp-filter">
                <span className="rn-muted">From</span>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={customFrom}
                  onChange={(e) => changeFilters(() => setCustomFrom(e.target.value))}
                />
              </label>
              <label className="cmp-filter">
                <span className="rn-muted">To</span>
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={customTo}
                  onChange={(e) => changeFilters(() => setCustomTo(e.target.value))}
                />
              </label>
            </>
          ) : null}

          <label className="cmp-filter">
            <span className="rn-muted">Activity</span>
            <select
              style={{ marginBottom: 0 }}
              value={category}
              onChange={(e) =>
                changeFilters(() => {
                  setCategory(e.target.value);
                  // The chosen action almost certainly belongs to the category
                  // being left, and a stale action would silently override the
                  // new category on the wire.
                  setAction('');
                })
              }
            >
              <option value="">All activity</option>
              {/* The `filters` query had no loading or error branch: on failure
                  these three selects silently collapsed to their "all" option,
                  which on a compliance screen reads as an organisation with no
                  recorded activity categories, no actors and no actions. The
                  note below the row says which it is. */}
              {(filters.data?.categories ?? []).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label} ({formatCount(c.eventCount)})
                </option>
              ))}
            </select>
          </label>

          <label className="cmp-filter">
            <span className="rn-muted">Specific action</span>
            <select
              style={{ marginBottom: 0 }}
              value={action}
              disabled={actions.length === 0}
              onChange={(e) => changeFilters(() => setAction(e.target.value))}
            >
              <option value="">{category ? 'Everything in this activity' : 'Any action'}</option>
              {actions.map((a) => (
                <option key={a.action} value={a.action}>
                  {a.label} ({a.eventCount})
                </option>
              ))}
            </select>
          </label>

          <label className="cmp-filter">
            <span className="rn-muted">Person</span>
            <select
              style={{ marginBottom: 0 }}
              value={actorUserId}
              disabled={actorNote != null}
              onChange={(e) => changeFilters(() => setActorUserId(e.target.value))}
            >
              <option value="">Anyone</option>
              {(filters.data?.actors ?? []).map((a) => (
                <option key={a.userId} value={a.userId}>
                  {a.label} ({a.eventCount})
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* An emptied actor list and an honestly empty one are the same `[]`.
            Without this the select collapsed to "Anyone" and read as an
            organization where nobody had done anything. */}
        {actorNote ? (
          <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
            {actorNote}
          </p>
        ) : null}

        {filtersUnavailable ? (
          <p className="rn-muted" role="status" style={{ fontSize: 13 }}>
            We couldn’t load the filter choices, so the three menus above offer only their
            unfiltered option. Your organization’s activity categories have not gone away.
          </p>
        ) : null}

        <AuditExport
          query={baseQuery}
          scope={exportScopeLabel(preset, { categoryLabel, actorLabel, actionLabel })}
        />

        {list.isLoading && pages.length === 0 ? (
          <SkeletonSet surface="table" rows={6} />
        ) : list.isError && pages.length === 0 ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load the activity log just now."
            onRetry={() => void list.refetch()}
          />
        ) : events.length === 0 ? (
          <SuccessEmpty
            heading={EMPTY_COPY[emptyReason(baseQuery)].heading}
            message={EMPTY_COPY[emptyReason(baseQuery)].message}
          />
        ) : (
          <>
            {/* WHAT THE NAMES IN THIS LOG DO AND DO NOT CLAIM, ONCE, FOR THE
                WHOLE PAGE.

                `AuditPage.labelNotice` is on EVERY page and was read by
                nothing. The CSV export has carried it since the projection
                grew it — `Record names` in the provenance block — so the
                disclosure reached a customer only if they downloaded the file.
                The person reading the same events on screen was shown
                live-resolved names with nothing saying they were live, and a
                compliance reader is exactly the audience who assumes the
                opposite.

                THE SERVER'S SENTENCE, VERBATIM, and no second phrasing beside
                it. `CURRENT_LABEL_NOTICE` (audit/projection.ts) is owned by the
                domain that owns the meaning, for the same reason
                `ATTESTATION_NOTICE` is: it cannot be softened, sharpened or
                deleted in a UI change without touching that file. Rendering it
                verbatim is what makes that guarantee real. The row-level half —
                `targetNote`, in the drawer — is the SAME statement narrowed to
                one record and dated with that row's `labelResolvedAt`; neither
                is a paraphrase of the other, and nothing here is composed.

                ONLY OVER ROWS. This sits inside the branch that renders events,
                so a filtered window with nothing in it does not carry a
                disclosure about names nobody was shown — the same rule
                `targetNote` follows when a row has no name to qualify.

                WHICH PAGE’S SENTENCE, WHEN SEVERAL PAGES ARE ON SCREEN.
                This read `lastPage.labelNotice` — the notice from the page
                that arrived LAST — over rows that accumulate FORWARD. Every
                earlier page’s rows stay on screen, and their own disclosure
                was discarded the moment "Load older activity" was pressed:
                a later page’s sentence silently replaced an earlier one for
                rows the reader had already read past.

                LATENT, AND SAID SO PLAINLY. `CURRENT_LABEL_NOTICE`
                (audit/projection.ts) is a module constant stamped on every
                page, so no two pages can disagree today. The smallest
                correct fix is therefore to make that invariant EXPLICIT
                rather than to build a reconciliation nobody needs: the
                DISTINCT notices across the pages on screen, in arrival
                order. One notice is one paragraph — every case that exists,
                and byte for byte what this rendered before. If the server
                ever sends two, both stand and neither is dropped, because a
                disclosure about rows a reader can still see is not the
                newest page’s to withdraw. */}
            {labelNotices(pages).map((notice) => (
              <p key={notice} className="rn-muted" role="note" style={{ fontSize: 13 }}>
                {notice}
              </p>
            ))}

            {grouped.map((group) => (
              <section key={group.heading} className="cmp-day">
                <h2 className="cmp-day-heading">{group.heading}</h2>
                <ul className="cmp-events">
                  {group.events.map((event) => {
                    const badge = sensitivityBadge(event.sensitivity);
                    return (
                      // No class: `.cmp-events` is a grid whose `gap` spaces
                      // these rows and `.cmp-event-btn` inside is already
                      // full-width, so the item needs no rule of its own.
                      <li key={event.id}>
                        <button
                          type="button"
                          className="cmp-event-btn"
                          onClick={() => setSelected(event)}
                          aria-label={`${event.summary}. ${formatEventTime(event.occurredAt)}. Open details.`}
                        >
                          <span className="cmp-event-summary">{event.summary}</span>
                          <span className="cmp-event-meta">
                            <span>{formatEventTime(event.occurredAt)}</span>
                            <span aria-hidden="true">·</span>
                            <span>{event.categoryLabel}</span>
                            {badge ? (
                              <Pill variant={badge.variant} dot>
                                {badge.label}
                              </Pill>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}

            <div className="cmp-more">
              <span className="rn-muted">
                {events.length} {events.length === 1 ? 'event' : 'events'} shown
              </span>
              {lastPage?.nextCursor ? (
                <button
                  type="button"
                  className="rn-btn-quiet"
                  disabled={list.isFetching}
                  onClick={() => setCursor(lastPage.nextCursor ?? undefined)}
                >
                  {list.isFetching ? 'Loading…' : 'Load older activity'}
                </button>
              ) : (
                <span className="rn-muted">You’ve reached the start of this period.</span>
              )}
            </div>
          </>
        )}
      </SettingsCard>

      {selected ? <EventDetail event={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

/**
 * THE AUDIT EXPORT, AND WHY IT IS NOT AN ANCHOR ANY MORE.
 *
 * This bar used to be `<a href={audit.exportUrl(baseQuery)} download>`. The
 * browser fetched the file with the session cookie and the bytes never passed
 * through JavaScript, which is a real virtue and is why it was written that
 * way. It has one cost, and for a compliance extraction the cost is the whole
 * point of the screen: AN ANCHOR CANNOT READ THE ANSWER.
 *
 * Three things the server says about this export were therefore unsayable:
 *
 *   - it was CAPPED. `/audit/export.csv` is bounded and synchronous by design
 *     and flags a range it could not fit. A reader who does not know their
 *     evidence file stops early has a short file that looks complete.
 *   - it was EMPTY. A header-only CSV and a capped one are the same artefact
 *     to somebody opening it in a spreadsheet a day later.
 *   - it FAILED. This is the one that costs something. A 403, a 500 or a
 *     dropped connection navigates the browser to an error body and leaves
 *     this screen saying nothing whatever — a silence indistinguishable from a
 *     download the operator simply has not found yet.
 *
 * So the file comes back through `audit.exportCsv`, which throws on every
 * non-2xx, and `downloadTextFile` hands it to the browser. ORDER MATTERS and is
 * the same order `ExportCsvButton` was corrected into: nothing on screen claims
 * anything about the file until the browser has been given it, because the
 * server confirming the rows is not the event this bar is about.
 *
 * NO `reporting:export` GATE, DELIBERATELY. `ExportCsvButton` renders nothing
 * without that key, which is correct for the admin-views catalog — it is the
 * key those routes guard. `/audit/export.csv` guards on `audit:read`, the same
 * key that opened this route, so requiring a second key here would gate a
 * capability on a permission the server does not check. That is the exact
 * defect `gate-keys.test.ts` exists for, and a `supervisor` — who holds
 * `reporting:export` and not `audit:read` — cannot reach this screen at all.
 */
function AuditExport({ query, scope }: { query: AuditNS.AuditQuery; scope: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<AuditNS.AuditCsvExport | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    setLast(null);
    try {
      const result = await audit.exportCsv(query);
      downloadTextFile(result.filename ?? 'audit-log.csv', result.text);
      setLast(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cmp-exportbar">
      <button type="button" className="rn-btn-quiet" disabled={busy} onClick={() => void run()}>
        {busy ? 'Exporting…' : 'Export CSV'}
      </button>
      <span className="rn-muted" style={{ fontSize: 13 }}>
        {scope}
      </span>
      {error ? (
        <span className="rn-muted" role="alert">
          Couldn’t export: {error}
        </span>
      ) : null}
      {last ? (
        <span className="rn-muted" role="status">
          {exportOutcome(last)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The disclosures covering the rows CURRENTLY ON SCREEN — distinct, in the
 * order they arrived. See the note at the render site for why this is not
 * simply the last page’s.
 */
function labelNotices(pages: AuditNS.AuditPage[]): string[] {
  return [...new Set(pages.map((page) => page.labelNotice).filter((n) => n.length > 0))];
}

/**
 * The event detail drawer.
 *
 * Shows only what the server projected. There is no raw payload, no identifier
 * dump, and no request/trace metadata — those are Phibian's operational
 * concerns, not a customer's compliance evidence.
 */
function EventDetail({ event, onClose }: { event: AuditNS.AuditEvent; onClose: () => void }) {
  const badge = sensitivityBadge(event.sensitivity);
  const hint = actorHint(event.actor);
  const note = targetNote(event.target);
  // `aria-modal="true"` claims the rest of the page is inert. The onKeyDown
  // below only fired while focus was already inside, and Tab could walk out of
  // the drawer into a page the dialog had just told AT to ignore.
  const ref = useFocusTrap<HTMLDivElement>(true, { onEscape: onClose });

  return (
    <div
      ref={ref}
      className="cmp-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Activity details"
    >
      <div className="cmp-drawer-head">
        <h2 className="rn-card-title">Activity details</h2>
        <button type="button" className="rn-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>

      <p className="cmp-drawer-summary">{event.summary}</p>
      {event.note ? <p className="rn-muted">{event.note}</p> : null}
      {badge ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
          <Pill variant={badge.variant} dot>
            {badge.label}
          </Pill>{' '}
          {badge.meaning}
        </p>
      ) : null}

      <dl className="cmp-facts">
        <div>
          <dt>When</dt>
          <dd>{formatEventTime(event.occurredAt)}</dd>
        </div>
        <div>
          <dt>Who</dt>
          <dd>
            {event.actor.label}
            {hint ? <span className="rn-muted"> — {hint}</span> : null}
          </dd>
        </div>
        <div>
          <dt>What it affected</dt>
          <dd>
            {targetLine(event.target)}
            {note ? <span className="rn-muted"> — {note}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Activity type</dt>
          <dd>{event.categoryLabel}</dd>
        </div>
      </dl>

      {event.details.length > 0 ? (
        <>
          <h3 className="rn-card-title" style={{ marginTop: 'var(--space-16)' }}>
            Recorded with this activity
          </h3>
          <dl className="cmp-facts">
            {event.details.map((d) => (
              <div key={d.label}>
                <dt>{d.label}</dt>
                <dd>{d.value}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <p className="rn-muted" style={{ marginTop: 'var(--space-16)', fontSize: 13 }}>
          Nothing further was recorded with this activity.
        </p>
      )}
    </div>
  );
}
